import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNotesStore } from '../../store/notes';

interface NotesSidebarProps {
  variant?: 'desktop' | 'mobile';
  selectedNoteId?: string | null;
  onSelectNote?: (id: string) => void | Promise<void>;
  onCreateNote?: (id: string) => void | Promise<void>;
  onDeleteNote?: (id: string) => void | Promise<void>;
}

const NotesSidebar: React.FC<NotesSidebarProps> = ({
  variant = 'desktop',
  selectedNoteId,
  onSelectNote,
  onCreateNote,
  onDeleteNote,
}) => {
  const { notes, activeNote, loading, openNote, createNote, deleteNote } =
    useNotesStore();
  const theme = useTheme();
  const isCompactViewport = useMediaQuery(theme.breakpoints.down('md'));
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const isMobile = variant === 'mobile';
  const resolvedSelectedId = selectedNoteId ?? activeNote?.id ?? null;

  const filteredNotes = React.useMemo(() => {
    const q = search.toLowerCase();
    const filtered = q
      ? notes.filter((n) => n.title.toLowerCase().includes(q))
      : notes;
    return [...filtered].sort(
      (a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime()
    );
  }, [notes, search]);

  const handleCreate = async () => {
    const note = await createNote('Untitled');
    if (!note) {
      return;
    }

    if (onCreateNote) {
      await onCreateNote(note.id);
      return;
    }

    await openNote(note.id);
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteNote(deleteTarget);
      if (onDeleteNote) {
        await onDeleteNote(deleteTarget);
      }
      setDeleteTarget(null);
    }
  };

  const handleSelect = async (id: string) => {
    if (onSelectNote) {
      await onSelectNote(id);
      return;
    }

    await openNote(id);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <Box
      sx={{
        width: isMobile ? '100%' : 260,
        flexShrink: 0,
        borderRight: isMobile ? 0 : 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: isMobile ? 2 : 1.5,
          py: isMobile ? 1.25 : 1,
          borderBottom: 1,
          borderColor: 'divider',
          gap: 1,
        }}
      >
        <Typography variant={isMobile ? 'h6' : 'subtitle2'} sx={{ minWidth: 0 }}>
          Notes
        </Typography>
        <IconButton
          size={isMobile ? 'medium' : 'small'}
          onClick={handleCreate}
          title="New note"
          aria-label="Create note"
          sx={{
            width: isMobile ? 44 : 32,
            height: isMobile ? 44 : 32,
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ px: isMobile ? 2 : 1, py: isMobile ? 1 : 0.5 }}>
        <TextField
          size={isMobile ? 'medium' : 'small'}
          fullWidth
          placeholder="Search notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {/* Note list */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading && notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Loading…
          </Typography>
        ) : filteredNotes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {search ? 'No matching notes' : 'No notes yet'}
          </Typography>
        ) : (
          <List dense={!isMobile} disablePadding>
            {filteredNotes.map((note) => (
              <ListItem
                key={note.id}
                disablePadding
                secondaryAction={
                  <IconButton
                    edge="end"
                    size={isMobile ? 'medium' : 'small'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(note.id);
                    }}
                    aria-label={`Delete ${note.title}`}
                    sx={{
                      opacity: isMobile ? 0.75 : 0.3,
                      width: isMobile ? 44 : 32,
                      height: isMobile ? 44 : 32,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemButton
                  selected={resolvedSelectedId === note.id}
                  onClick={() => void handleSelect(note.id)}
                  alignItems="flex-start"
                  sx={{
                    px: isMobile ? 2 : 1.5,
                    py: isMobile ? 1.25 : 0.75,
                    pr: isMobile ? 7 : 6,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          overflow: 'hidden',
                          display: isMobile ? '-webkit-box' : 'block',
                          textOverflow: 'ellipsis',
                          whiteSpace: isMobile ? 'normal' : 'nowrap',
                          WebkitLineClamp: isMobile ? 2 : 'unset',
                          WebkitBoxOrient: isMobile ? 'vertical' : 'initial',
                          wordBreak: 'break-word',
                          pr: 0.5,
                        }}
                      >
                        {note.title}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        {formatDate(note.modified)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Delete Confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: '100%' },
            maxWidth: { xs: 'calc(100% - 32px)', sm: undefined },
            borderRadius: { xs: 3, sm: 2 },
            m: { xs: 2, sm: 4 },
          },
        }}
      >
        <DialogTitle>Delete Note?</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography variant="body2">
            This will permanently delete the note from Google Drive.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 3, sm: 2 },
            pb: { xs: 3, sm: 2 },
            pt: 1,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            '& > :not(style)': {
              ml: { xs: '0 !important', sm: 1 },
              width: { xs: '100%', sm: 'auto' },
            },
          }}
        >
          <Button
            onClick={() => setDeleteTarget(null)}
            size={isCompactViewport ? 'large' : 'medium'}
            sx={{ minHeight: { xs: 44, md: 36 } }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            size={isCompactViewport ? 'large' : 'medium'}
            sx={{ minHeight: { xs: 44, md: 36 } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotesSidebar;
